/**
 * ReleaseCard Component
 * 
 * Enhanced release card with detailed information, better styling,
 * and improved visual hierarchy for the timeline view.
 */

import React, { useState } from 'react'
import { Box, Typography, Paper, useTheme } from '@mui/material'
import { CategorizedRelease } from '../../types/release'
import { RELEASE_TIMELINE_COLORS, getCategoryColors } from '../../styles/releaseTimelineStyles'
import { formatDisplayDate, isOverdueRelease, isUpcomingRelease } from '../../utils/releaseDateFiltering'
import ReleaseTooltip from './ReleaseTooltip'
import { calculateOptimalTextLayout } from '../../utils/dualTrackPositioning'

// ============================================================================
// COMPONENT PROPS
// ============================================================================

export interface ReleaseCardProps {
  /** Release data to display */
  release: CategorizedRelease

  /** Position within the timeline */
  position: {
    left: number
    width: number
    top?: number
  }

  /** Height of the containing swimlane */
  swimlaneHeight: number

  /** Callback when card is clicked */
  onClick?: () => void

  /** Whether to enable interactions */
  enableInteractions?: boolean

  /** Whether to show detailed information */
  showDetails?: boolean

  /** Track information for dual-track layout */
  trackInfo?: {
    trackNumber: number
    totalTracks: number
  }
}

// ============================================================================
// RELEASE CARD COMPONENT
// ============================================================================

const ReleaseCard: React.FC<ReleaseCardProps> = ({
  release,
  position,
  swimlaneHeight,
  onClick,
  enableInteractions = true,
  showDetails = true,
  trackInfo
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const theme = useTheme()
  const isDarkMode = theme.palette.mode === 'dark'

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const category = release.category!
  const categoryColors = getCategoryColors(category, isDarkMode)

  // Calculate card dimensions and positioning - Dual-track design
  const cardHeight = Math.min(swimlaneHeight - 10, 45) // Compact height: max 45px
  const cardTop = position.top !== undefined ? position.top : (swimlaneHeight - cardHeight) / 2
  const minWidth = 120 // Minimum width for readability
  const cardWidth = Math.max(position.width - 8, minWidth)

  // Smart text layout calculation for optimal display
  const textLayout = calculateOptimalTextLayout(release.displayName || '', 11)

  // Status logic for dot color
  const getStatusColor = () => {
    if (release.released) {
      return RELEASE_TIMELINE_COLORS.status.released // Green - released
    }

    if (isOverdueRelease(release)) {
      return RELEASE_TIMELINE_COLORS.status.overdue // Red - overdue
    }

    return RELEASE_TIMELINE_COLORS.status.unreleased // Orange - not released but within due date
  }

  // No connector lines - removed to eliminate horizontal separators
  
  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================
  
  const handleMouseEnter = () => {
    if (!enableInteractions) return
    setIsHovered(true)
  }
  
  const handleMouseLeave = () => {
    if (!enableInteractions) return
    setIsHovered(false)
  }
  
  const handleClick = () => {
    if (!enableInteractions) return
    onClick?.()
  }
  
  // ============================================================================
  // RENDER
  // ============================================================================
  
  return (
    <ReleaseTooltip
      release={release}
      showDetails={true}
      showKeywords={false}
      trackInfo={trackInfo}
    >
      <Box
        sx={{
          position: 'absolute',
          left: position.left,
          top: cardTop,
          zIndex: isHovered ? 10 : 1
        }}
      >
        {/* Release Card */}
        <Paper
          elevation={isHovered ? 3 : 1}
          sx={{
            position: 'absolute',
            left: 0,
            width: cardWidth,
            height: cardHeight,
            backgroundColor: isDarkMode ? '#2d2d2d' : 'white',
            borderRadius: 1.5,
            cursor: enableInteractions ? 'pointer' : 'default',
            transition: 'all 0.2s ease-in-out',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            px: 1,
            py: 0.5,
            border: `1px solid ${categoryColors.primary}40`,
            ...(isHovered && {
              transform: 'scale(1.02)',
              boxShadow: `0 2px 8px ${categoryColors.primary}30`,
              borderColor: categoryColors.primary,
              backgroundColor: isDarkMode ? '#3d3d3d' : 'white'
            })
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
        >
          {/* Category Color Indicator */}
          <Box
            sx={{
              width: 4,
              height: cardHeight - 8,
              backgroundColor: categoryColors.primary,
              borderRadius: 2,
              mr: 1,
              flexShrink: 0
            }}
          />

          {/* Card Content */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minWidth: 0, // Allow text truncation
              gap: 0.25,
              position: 'relative' // For absolute positioning of status dot
            }}
          >
            {/* Release Name - Smart 2-line display */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minHeight: textLayout.displayLines.length > 1 ? '20px' : '12px'
            }}>
              {textLayout.displayLines.map((line, index) => (
                <Typography
                  key={index}
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    fontSize: textLayout.displayLines.length > 1 ? '0.65rem' : '0.7rem',
                    lineHeight: textLayout.displayLines.length > 1 ? 1.0 : 1.1,
                    color: categoryColors.text,
                    overflow: 'visible',
                    whiteSpace: 'nowrap',
                    textAlign: 'left'
                  }}
                >
                  {line}
                </Typography>
              ))}
            </Box>

            {/* Release Date and Status Indicator Row */}
            {release.releaseDate && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 0.5
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.65rem',
                    lineHeight: 1,
                    color: 'text.secondary',
                    fontWeight: 500
                  }}
                >
                  {formatDisplayDate(release.releaseDate)}
                </Typography>

                {/* Status Indicator - positioned at bottom-right */}
                {release.released !== undefined && (
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: getStatusColor(),
                      flexShrink: 0
                    }}
                  />
                )}
              </Box>
            )}

            {/* Status Indicator for releases without date - fallback to original position */}
            {!release.releaseDate && release.released !== undefined && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 2,
                  right: 2,
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  backgroundColor: getStatusColor(),
                  flexShrink: 0
                }}
              />
            )}
          </Box>
        </Paper>
      </Box>
    </ReleaseTooltip>
  )
}

// ============================================================================
// MEMOIZED EXPORT
// ============================================================================

export default React.memo(ReleaseCard)

// ============================================================================
// COMPONENT DISPLAY NAME
// ============================================================================

ReleaseCard.displayName = 'ReleaseCard'
